/**
 * DeclineEngagementModal Component
 * Feature 014: Service Provider Engagements
 *
 * Modal dialog for service providers to decline engagements
 * with a required reason that will be sent as a message to the care seeker
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { AlertCircle } from 'lucide-react';

type DeclineEngagementModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  careSeekerName: string;
  onConfirm: (reason: string) => void;
  isSubmitting?: boolean;
};

const MAX_REASON_LENGTH = 500;

export function DeclineEngagementModal({
  open,
  onOpenChange,
  careSeekerName,
  onConfirm,
  isSubmitting = false,
}: DeclineEngagementModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setReason('');
      setError('');
    }
  }, [open]);

  const validateForm = (): boolean => {
    if (!reason.trim()) {
      setError('Please provide a reason for declining');
      return false;
    }

    if (reason.length > MAX_REASON_LENGTH) {
      setError(`Reason must be ${MAX_REASON_LENGTH} characters or less`);
      return false;
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    onConfirm(reason.trim());
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const remainingChars = MAX_REASON_LENGTH - reason.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Decline Consultation Request</DialogTitle>
          <DialogDescription>
            You're declining the consultation request from {careSeekerName}. Your reason will be sent to them
            as a message.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Error message */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Reason field */}
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason for declining <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reason"
                placeholder="E.g., 'Thank you for reaching out. Unfortunately, I'm not currently accepting new clients in your area.' or 'I don't provide the specific services you're looking for, but I'd be happy to refer you to someone who does.'"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={5}
                disabled={isSubmitting}
                className="resize-none"
              />
              <p className="text-sm text-gray-500">{remainingChars} characters remaining</p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This message will be sent directly to {careSeekerName} and the consultation request will be
                removed from your pending list.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isSubmitting ? 'Declining...' : 'Decline Consultation Request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
